import React, { useState, useEffect, Fragment } from 'react';
import { Content } from 'styles/globalStyles';
import { TitleBar } from 'styles/layout/titlebar';
import SendGridService from 'services/sendgrid';
import { useDispatch } from 'react-redux';
import useTitle from 'hooks/useTitle';
import JsxHelper from 'helpers/jsx';
import env from 'config/env';
import WPSDataTable from 'components/wpstaq/WPSDataTable/WPSDataTable';
import DialogHelper from 'helpers/dialog';
import StringHelper from 'helpers/string';
import SendGridHelper from 'helpers/sendgrid';
import { displayResponseError, isEmptyOrNull } from 'helpers';
import useModal from 'hooks/useModal';
import useConfirm from 'hooks/useConfirm';
import 'components/stepper/stepper.css';
import UserHelper from 'helpers/user';

const SendGrid = () => {
  useTitle('SendGrid');
  const breadcrumbs = JsxHelper.createBreadcrumbs('SendGrid', 'settings');
  const dispatch = useDispatch();
  const [modalLoading, setModalLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [verifyDomainLoading, setVerifyDomainLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const modalDialog = useModal();
  const [accounts, setAccounts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [domainsMode, setDomainsMode] = useState(false);
  const [myIPAddress, setMyIPAddress] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null); // {guid: '', email: ''}
  const confirm = useConfirm();

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line
  }, []);

  const handleErrorResponse = (error) => displayResponseError(modalDialog, error);

  // ------------------ ACCOUNT ------------------

  const fetchAccounts = () => {
    setTableLoading(true);
    SendGridService.listAccounts()
      .then(data => setAccounts(data))
      .catch(handleErrorResponse)
      .finally(() => setTableLoading(false))
  };

  const connectAccount = () => {
    setModalLoading(true);
    SendGridService.connectAccount({ api_key: modal.api_key })
      .then(newAccount => {
        setAccounts(prev => [...prev, newAccount]);
        setModal(false);
      })
      .catch(handleErrorResponse)
      .finally(() => setModalLoading(false))
  };

  const disconnectAccount = account => {
    setTableLoading(true);
    SendGridService.disconnectAccount({ guid: account.guid })
      .then(() => setAccounts(prev => prev.filter(item => item.guid !== account.guid)))
      .catch(handleErrorResponse)
      .finally(() => setTableLoading(false))
  }

  const refreshAccount = account => {
    setTableLoading(true);
    SendGridService.refreshAccount({ guid: account.guid })
      .then(data => setAccounts(prev => prev.map(item => item.guid === data.guid ? data : item)))
      .catch(handleErrorResponse)
      .finally(() => setTableLoading(false))
  }

  const whitelistIPs = () => {
    setModalLoading(true);
    const ips = modal.ips.split(',').map(ip => ip.trim()).filter(ip => ip);
    SendGridService.addIPsToAllowList({ ips, guid: modal.guid })
      .then((account) => {
        setAccounts(prev => prev.map(a => a.guid === account.guid ? account : a));
        setModal(false);
      })
      .catch(handleErrorResponse)
      .finally(() => setModalLoading(false))
  }

  const backToAccounts = () => {
    setDomains(false);
    setDomainsMode(false);
  }

  const accountActions = [
    {
      value: 'IP Access Management',
      doHide: () => !UserHelper.isPartner(),
      onClick: (item) => {
        // Fetch the current IP address
        if (!myIPAddress) {
          fetch('https://api.ipify.org?format=json')
          .then(res => res.json())
          .then(data => setMyIPAddress(data.ip))
          .catch((err) => window.logHelper.error('Error fetching IP address', err));
        }
        // Set the modal with the current IP address
        setModal({
          name: 'whitelist-ips',
          ips: (item.user_allowed_ips || []).join(', '),
          confirm: false,
          guid: item.guid,
        })
      }
    },
    {
      value: 'Refresh Account',
      onClick: refreshAccount,
    },
    {
      value: 'Authenticated Domains',
      doHide: item => isEmptyOrNull(item.auth_domains),
      onClick: item => {
        setCurrentAccount(item);
        setDomains(item.auth_domains);
        setDomainsMode(true);
      }
    },
    {
      value: 'Disconnect',
      onClick: item => DialogHelper
        .confirmAction(confirm, 'disconnect', item.name, 'SendGrid Account', `This will disconnect your account from the ${env.getBrandShortName()} dashboard, but it will not affect the account on SendGrid's end.`)
        .then(() => disconnectAccount(item))
    },
  ];

  const accountHeaders = [
    {
      name: 'Account',
      selector: 'email',
      searchable: true,
      width: '35%',
      cell: row => JsxHelper.createTableMultiLineCell({
        header: row.email,
      })
    },
    JsxHelper.createTableTextHeaderWithCallback('type', 'Authenticated Domains', '30%', (row) => {
      const authDomains = row.auth_domains;
      if (isEmptyOrNull(authDomains)) return 'No domains';
      const valid = authDomains.filter(d => d.valid).length;
      const pending = authDomains.length - valid;
      return <div>
        {valid ? JsxHelper.createIconWithText({
          className: 'margin-top-5',
          icon: 'success',
          textColor: 'success',
          text: `${valid} verified ${StringHelper.maybePluraize('domain', valid)}`,
        }) : null}
        {pending ? JsxHelper.createWarningBox(`${StringHelper.maybePluraize('domain', pending, true)} pending verification`, true) : null}
      </div>
    }),
    JsxHelper.createTableTextHeaderWithCallback('type', 'IP Access Management', '25%', (row) => {
      if (!row.allowed_ips_enabled) {
        return JsxHelper.createBubble({
          text: 'Disabled',
          customClass: 'disabled--btn',
          small: true,
          tooltip: 'IP Access Management is disabled for this account. You can enable it in the dropdown menu on the right by clicking on "Allow Listed IPs".',
        });
      }
      return <span>
        {(row.user_allowed_ips || []).map(ip => (
          <div key={ip} className='margin-top-5'>
            {JsxHelper.createCopyButton({ value: ip, small: true, dispatch })}
          </div>
        ))}
      </span>
    }),
    JsxHelper.createTableActionsHeader(accountActions, '10%'),
  ];

  // ------------------ AUTHENTICATED DOMAIN ------------------

  const authenticateDomain = () => {
    setModalLoading(true);
    SendGridService.authenticateDomain({ guid: currentAccount.guid, domain: modal.domain, automated_security: modal.automated_security })
      .then((account) => {
        setAccounts(prev => prev.map(a => a.guid === account.guid ? account : a));
        setDomains(account.auth_domains);
        setCurrentAccount(account);
        setModal(false);
      })
      .catch(handleErrorResponse)
      .finally(() => setModalLoading(false))
  }

  const verifyDomainAuth = (item) => {
    setVerifyDomainLoading(item.id);
    SendGridService.verifyDomainRecords({ guid: currentAccount.guid, domain_id: item.id })
      .then((account) => {
        setAccounts(prev => prev.map(a => a.guid === account.guid ? account : a));
        setDomains(account.auth_domains);
        setCurrentAccount(account);
      })
      .catch(handleErrorResponse)
      .finally(() => setVerifyDomainLoading(false))
  }

  const domainsActions = [
    {
      value: 'Verify Domain',
      onClick: verifyDomainAuth,
      doHide: item => item.valid,
    },
    {
      value: 'DNS Records',
      onClick: (item) => SendGridHelper.openDNSRecordsModal(modalDialog, dispatch, verifyDomainAuth, item),
    },
  ];

  const domainsHeaders = [
    JsxHelper.createTableTextHeaderWithCallback('domain', 'Domain', '50%', (row) => {
      const fullDomain = `${row.subdomain}.${row.domain}`;
      return JsxHelper.createLink(`${SendGridHelper.SENDGRID_APP_URL}/settings/sender_auth/domain/get/${row.id}`, fullDomain, 'class-link no-underline font-bold')
    }),
    JsxHelper.createTableTextHeaderWithCallback('status', 'Status', '20%', (row) =>
      verifyDomainLoading === row.id
      ? JsxHelper.createLoadingSpinner('Verifying...', 15, '0 10px 0 0', 0, { position: 'relative', top: '-2px' })
      : JsxHelper.createIconWithText({
          icon: row.valid ? 'success' : 'danger',
          textColor: row.valid ? 'success' : 'danger',
          text: row.valid ? 'Verified' : 'Failed',
        })
    ),
    JsxHelper.createTableActionsHeader(domainsActions, '30%'),
  ];

  return (
    <div className='global-settings'>
      <TitleBar className='titlebar'>
        <TitleBar.Title breadcrumbs={domainsMode ? breadcrumbs.concat([{text: 'Accounts', link: '#'}]) : breadcrumbs}>
          {domainsMode ? currentAccount.email : 'SendGrid Accounts'}
        </TitleBar.Title>
        <TitleBar.Actions>
          {domainsMode && JsxHelper.createButton({
            label: 'Authenticate Domain',
            onClick: () => setModal({ name: 'create-domain', domain: '', automated_security: true } ),
            classes: 'success--btn'}
          )}
          {!domainsMode && JsxHelper.createButton({
            label: 'Connect Account',
            onClick: () => setModal({ name: 'create-account', api_key: '' })}
          )}
          {!domainsMode ? JsxHelper.createBackButton() : JsxHelper.createButton({
            label: 'Back',
            onClick: backToAccounts
          })}
        </TitleBar.Actions>
      </TitleBar>
      {!domainsMode && <Fragment>
        <p className='color-primary subheader'>
          {env.getBrandShortName()} offers a seamless integration with <b>{JsxHelper.createLink('https://sendgrid.com', 'SendGrid')}</b> that enables you to configure your email settings directly from the dashboard.
          For more information, please refer to our {JsxHelper.createFaqLink('integrating-sendgrid-with-staq', 'knowledge base')}.
        </p>
        {!isEmptyOrNull(accounts) && !accounts.some(account => account.allowed_ips_enabled) && <p className='subheader subheader-box'>
          {JsxHelper.createTipBox(<Fragment>Enable {JsxHelper.createFaqLink('sendgrid-ip-access-management', 'IP Access Management')} from the right-hand menu to enhance SendGrid security.</Fragment>)}
        </p>}
      </Fragment>}
      {domainsMode && <p className='color-primary subheader'>
        Manage your authenticated domains for the <b>{currentAccount.email}</b> SendGrid account.
      </p>}
      <Content>
        {domainsMode && <div id='sg-domains' style={{width: '75%'}}>
          <WPSDataTable
            columns={domainsHeaders}
            body={domains}
            noSearchOnTable={true}
            hidePagination={true}
            loading={tableLoading}
          />
        </div>}
        {!domainsMode && <div id='sg-accounts' style={{width: '75%'}}>
          <WPSDataTable
            columns={accountHeaders}
            body={accounts}
            noSearchOnTable={true}
            hidePagination={true}
            loading={tableLoading}
          />
        </div>}
      </Content>
      {modal && modal.name === 'create-account' && DialogHelper.inputs({
        title: 'Connect SendGrid Account',
        className: 'instruct-modal',
        onClose: () => setModal(false),
        onConfirm: connectAccount,
        loading: modalLoading,
        icon: 'sendgrid',
        iconColor: 'info',
        confirmBtn: 'Connect',
        confirmColor: 'success',
        disabled: isEmptyOrNull(modal.api_key),
        header: SendGridHelper.renderConnectSteps(),
        inputs: [{
          label: 'API Key',
          name: 'api_key',
          placeholder: 'Enter your SendGrid API key',
          value: modal.api_key,
          onChange: e => setModal({ ...modal, api_key: e.target.value }),
          required: true,
        },
      ]})}
      {modal && modal.name === 'create-domain' && DialogHelper.inputs({
        title: 'Authenticate Your Domain',
        onClose: () => setModal(false),
        onConfirm: authenticateDomain,
        loading: modalLoading,
        iconColor: 'info',
        confirmBtn: 'Create',
        confirmColor: 'success',
        disabled: isEmptyOrNull(modal.domain),
        header: JsxHelper.createTipBox(
          'Domain authentication removes the "via sendgrid.net" text that some inbox providers append to your from address.',
          true
        ),
        inputs: [{
          label: 'From Domain',
          name: 'domain',
          placeholder: 'Enter the domain you send emails from', 
          value: modal.domain,
          onChange: e => setModal({ ...modal, domain: e.target.value }),
          required: true,
        }, {
          label: 'Automated Security',
          name: 'automated_security',
          type: 'checkbox',
          tooltip: 'When Enabled, we automatically rotate your DKIM keys so that they are impossible to break.',
          checked: modal.automated_security,
          onChange: e => setModal({ ...modal, automated_security: e.target.checked }),
        }]
      })}
      {modal && modal.name === 'whitelist-ips' && DialogHelper.inputs({
        title: 'Whitelist IPs',
        onClose: () => setModal(false),
        loading: modalLoading,
        icon: 'sendgrid',
        iconColor: 'info',
        confirmBtn: 'Save',
        confirmColor: 'success',
        className: 'min-width-35p',
        onConfirm: whitelistIPs,
        disabled: isEmptyOrNull(modal.ips) || isEmptyOrNull(modal.confirm),
        header: <Fragment>
          <p className='margin-top-12'>
            {JsxHelper.createWarningBox(`To access the SendGrid UI, please add your home or office IP address to the allowed IPs list. Your current IP address is shown below—simply copy and paste it into the field provided.`, false, 'Important')}
          </p>
          <p className='margin-top-24'>
          IP Access Management restricts SendGrid account access to specific IPs or CIDR ranges, enhancing security by blocking unauthorized access. Be sure to add your own IP to avoid being locked out.
          </p>
          <p className='margin-top-24'>
            Your current IP address is: {myIPAddress ? JsxHelper.createCopyButton({ value: myIPAddress, style: { display: 'inline' }, dispatch }) : JsxHelper.createLoadingSpinner()}
          </p>
        </Fragment>,
        inputs: [{
          label: 'IP Addresses / IP Ranges (CIDR)',
          name: 'ips',
          placeholder: 'e.g. 203.0.113.0/24, 198.51.100.42 (comma-separated)',
          labelTooltip: `All IP addresses related to ${env.getBrandShortName()} websites will be automatically added to this list, so you only need to add your own IPs here.`,
          value: modal.ips,
          onChange: e => setModal({ ...modal, ips: e.target.value }),
          required: true,
        }, {
          type: 'checkbox',
          name: 'confirm',
          label: 'I understand the risks of IP Access Management',
          checked: modal.confirm,
          onChange: e => setModal({ ...modal, confirm: e.target.checked }),
        }]
      })}
    </div>
  );
};

export default SendGrid;
